import { BlockchainNetwork, ConstantsHelper, SOL, WalletType } from "@renec-foundation/relend-adapter"
import BigNumber from "bignumber.js"

import { ObligationType } from "@/atoms/obligations"
import { SelectedReserveType } from "@/atoms/shared"
import { NATIVE_MINT } from "@solana/spl-token"
import { RENEC } from "../constants"
import { ObligationBorrowReserve } from "../dataTypes"
import { replaceLocaleStringWithValues } from "../helpers"
import { calculateBorrowInterest, Configuration } from "../rate"
import {
  FIAT_DECIMALS,
  RENEC_PADDING_FOR_RENT_AND_FEE,
  SOL_PADDING_FOR_RENT_AND_FEE,
  sufficientNativeTokenForTransaction,
  sufficientNativeTokenForTransactionWithValue,
} from "./commons"
import { calculateCompoundedInterestRate } from "./utils"

export const REPAY_BUFFER_SLOTS = parseInt(process.env.NEXT_PUBLIC_REPAY_BUFFER_SLOTS ?? "30", 10) || 30 // 30 seconds (2 slots/s)

export const repayConfigs = {
  verifyAction: (
    network: BlockchainNetwork,
    value: BigNumber,
    obligation: ObligationType | null,
    reserve: SelectedReserveType,
    wallet: WalletType,
    trans: any,
    currentSlot: number | null,
  ) => {
    // Check basic amount validity first
    if (value.isZero() || !value) {
      return trans.dashboard.modal.errors.please_enter_amount
    }

    if (!obligation || !currentSlot) return null

    const isFiatToken = reserve.decimals === FIAT_DECIMALS
    const roundingStrategy = isFiatToken ? BigNumber.ROUND_UP : undefined

    const reserveBorrowed = obligation.borrows.find((b) => b.reserveAddress === reserve.address)
    if (!reserveBorrowed) return null

    const { newBorrowedAmountAfterRefresh, compoundedInterestRateWhenRefreshObligation } =
      calculateNewBorrowedAmountAndCompoundedInterestRate(reserve, reserveBorrowed, currentSlot)

    const max = new BigNumber(
      formatToDecimals(
        newBorrowedAmountAfterRefresh.multipliedBy(compoundedInterestRateWhenRefreshObligation),
        reserve.decimals,
      ),
    )

    const valueRounded = value.decimalPlaces(reserve.decimals, roundingStrategy)

    const tokenBalance =
      wallet
        .find((a) => a.mintAddress === reserve.mintAddress)
        ?.amount.decimalPlaces(reserve.decimals, roundingStrategy) ?? BigNumber(0)

    if (valueRounded.isGreaterThan(tokenBalance) || tokenBalance.isZero()) {
      return trans.dashboard.modal.errors.insufficient_balance(reserve.symbol)
    }
    if (valueRounded.isGreaterThan(max)) {
      return trans.dashboard.modal.errors.amount_exceeded_borrowed
    }

    const isRenecChain = ConstantsHelper.isRenecChain(network)
    const isSolanaChain = ConstantsHelper.isSolanaChain(network)

    const amountPadding = isRenecChain ? RENEC_PADDING_FOR_RENT_AND_FEE : SOL_PADDING_FOR_RENT_AND_FEE
    const isRenecOrSolanaLiquidity =
      (reserve.symbol === RENEC && isRenecChain) || (reserve.symbol === SOL && isSolanaChain)
    const isInSufficient = isRenecOrSolanaLiquidity
      ? !sufficientNativeTokenForTransactionWithValue(network, wallet, valueRounded)
      : !sufficientNativeTokenForTransaction(network, wallet)
    const nativeMintBalance =
      wallet.find((ass) => ass.mintAddress === NATIVE_MINT.toBase58())?.amount ?? new BigNumber(0)
    const minNativeFee = isRenecOrSolanaLiquidity
      ? new BigNumber(amountPadding).minus(nativeMintBalance.minus(value)).toString()
      : new BigNumber(amountPadding).minus(nativeMintBalance).toString()

    if (isInSufficient) {
      return replaceLocaleStringWithValues(
        trans.dashboard.modal.errors.min_native_fee,
        minNativeFee,
        isRenecChain ? RENEC : SOL,
      )
    }

    return null
  },
  getNewCalculations: (obligation: ObligationType | null, reserve: SelectedReserveType, value: string) => {
    if (!obligation) {
      return {
        borrowLimit: null,
        newBorrowLimit: null,
        utilization: null,
        newBorrowUtilization: null,
        calculatedBorrowFee: null,
      }
    }

    const valueObj = new BigNumber(value)

    const utilization = !obligation.totalSupplyValue.isZero()
      ? obligation.totalBorrowValue.dividedBy(obligation.totalSupplyValue)
      : null

    const newBorrowBalance = BigNumber.max(
      obligation.totalBorrowValue.minus(valueObj.times(reserve.price)),
      BigNumber(0),
    )

    const newBorrowUtilization =
      obligation.totalSupplyValue && !obligation.totalSupplyValue.isZero()
        ? newBorrowBalance.dividedBy(obligation.totalSupplyValue)
        : null

    return {
      borrowLimit: obligation.minPriceBorrowLimit,
      newBorrowLimit: null,
      utilization: utilization,
      newBorrowUtilization,
      calculatedBorrowFee: null,
    }
  },
  calculateMax: (
    network: BlockchainNetwork,
    reserve: SelectedReserveType,
    wallet: WalletType,
    obligation: ObligationType | null,
    currentSlot: number | null,
  ) => {
    if (!obligation || !currentSlot) {
      return new BigNumber(0)
    }
    const isRenecChain = ConstantsHelper.isRenecChain(network)
    const isSolanaChain = ConstantsHelper.isSolanaChain(network)

    const asset = findAssetInWallet(wallet, reserve)
    if (!asset) return new BigNumber(0)

    const reserveBorrowed = obligation.borrows.find((b) => b.reserveAddress === reserve.address)
    if (!reserveBorrowed) return BigNumber(0)

    const isRenecOrSolanaLiquidity =
      (reserve.symbol === RENEC && isRenecChain) || (reserve.symbol === SOL && isSolanaChain)

    const maxRepayableFromWallet = isRenecOrSolanaLiquidity
      ? BigNumber.max(asset.amount.minus(RENEC_PADDING_FOR_RENT_AND_FEE), new BigNumber(0))
      : asset.amount

    const { newBorrowedAmountAfterRefresh } = calculateNewBorrowedAmountAndCompoundedInterestRate(
      reserve,
      reserveBorrowed,
      currentSlot,
    )

    // Multiply the new compoundedInterestRate after refresh obligation with the current max amount to output the estimated max amount to be submitted.
    // Example: current max is 1 token, after refresh new estimated max is 1.00001 token.
    const max = new BigNumber(
      formatToDecimals(BigNumber.min(newBorrowedAmountAfterRefresh, maxRepayableFromWallet), reserve.decimals),
    )

    return max
  },
}

const findAssetInWallet = (wallet: WalletType, reserve: SelectedReserveType) =>
  wallet.find((ass) => ass.mintAddress === reserve.mintAddress)

const createConfigShowingAPR = (reserve: SelectedReserveType) => {
  return {
    protocolTakeRate: reserve.interestRateSpread,
    isShowingAPY: false,
    borrowedAmount: reserve.totalBorrow,
    availableAmount: reserve.availableAmount,
    optimalUtilizationRate: reserve.targetUtilization,
    maxUtilizationRate: reserve.maxUtilizationRate,
    minBorrowRate: reserve.minBorrowApr,
    optimalBorrowRate: reserve.targetBorrowApr,
    maxBorrowRate: reserve.maxBorrowApr,
    superMaxBorrowRate: reserve.superMaxBorrowRate,
    accumulatedProtocolFees: reserve.accumulatedProtocolFeesWad,
    decimals: reserve.decimals,
  }
}

/**
 * The function is used to format to the decimals number with error value of 1 in the decimal place.
 * EG: with 9 decimal places
 *  - 1.1234567806 with decimals place = 9 will return 1.123456781 (got rounded to 9 decimal)
 *  - 1.1234567801 with decimals place = 9 will return 1.123456781 (Got added 1 because there is some value after
 * the decimal place)
 */
export function formatToDecimals(amount: BigNumber, decimals: number): BigNumber {
  const amountStr = amount.toString()

  // Split the input string into integer and decimal parts
  const [, decimalPart] = amountStr.split(".")

  // If the number of decimals in the original amount is less than or equal to the specified limit, return as is
  if (!decimalPart || decimalPart.length <= decimals) {
    return amount
  }

  // Round the amount to 9 decimal places
  let roundedAmount = amount.decimalPlaces(decimals)

  // Truncate the original amount to 9 decimal places without rounding
  const truncatedAmountStr = amountStr.split(".")[0] + "." + (amountStr.split(".")[1] || "").substring(0, decimals)
  const truncatedAmount = parseFloat(truncatedAmountStr)

  // Check if the original amount's decimal part is at least as long as the specified decimals
  const hasSufficientDecimals = (amountStr.split(".")[1] || "").length >= decimals

  // Check if the rounded amount is the same as the truncated amount
  if (roundedAmount.isEqualTo(truncatedAmount) && hasSufficientDecimals) {
    // If so, add the error value (0.000000001) to the rounded amount
    const increment = new BigNumber(1).dividedBy(new BigNumber(10).pow(decimals))
    roundedAmount = roundedAmount.plus(increment)
  }

  return roundedAmount
}

export const calculateNewBorrowedAmountAndCompoundedInterestRate = (
  reserve: SelectedReserveType,
  reserveBorrowed: ObligationBorrowReserve,
  currentSlot: number,
) => {
  const configShowingAPR: Configuration = createConfigShowingAPR(reserve)
  const currentBorrowRate = calculateBorrowInterest(configShowingAPR)
  const slotElapsed = currentSlot - (reserve.lastUpdateSlot.toNumber() ?? 0) + REPAY_BUFFER_SLOTS
  const compoundedInterestRateWhenRefreshReserve = calculateCompoundedInterestRate(currentBorrowRate, slotElapsed)
  const reserveBorrowedAmount = reserveBorrowed.borrowedAmountDecimals
  const reserveCummulativeBorrowRate = reserve.cumulativeBorrowRate
  const obligationCummulativeBorrowRate = reserveBorrowed.cumulativeBorrowRate
  const newReserveCummulativeBorrowRate = reserveCummulativeBorrowRate.multipliedBy(
    compoundedInterestRateWhenRefreshReserve,
  )
  const compoundedInterestRateWhenRefreshObligation = newReserveCummulativeBorrowRate.div(
    obligationCummulativeBorrowRate,
  )
  const newBorrowedAmountAfterRefresh = reserveBorrowedAmount.multipliedBy(compoundedInterestRateWhenRefreshObligation)
  return { newBorrowedAmountAfterRefresh, compoundedInterestRateWhenRefreshObligation }
}
